// ISO 3166-1 alpha-2 country code -> TravelMobile region.

const REGION_COUNTRIES = {
    'Australia': ['AU'],

    'New Zealand': ['NZ'],

    // Oceania minus AU/NZ.
    'Pacific': [
        'FJ', 'PG', 'WS', 'TO', 'VU', 'SB', 'NC', 'PF', 'GU', 'KI', 'FM', 'MH',
        'NR', 'PW', 'TV', 'CK', 'NU', 'AS', 'MP', 'WF', 'TK', 'PN', 'NF',
    ],

    // Asia, including the Middle East. Transcontinental countries are placed by
    // their most common single-continent classification (Turkey -> Asia).
    'Asia': [
        'AF', 'AM', 'AZ', 'BH', 'BD', 'BT', 'BN', 'KH', 'CN', 'GE', 'HK', 'IN',
        'ID', 'IR', 'IQ', 'IL', 'JP', 'JO', 'KZ', 'KW', 'KG', 'LA', 'LB', 'MO',
        'MY', 'MV', 'MN', 'MM', 'NP', 'KP', 'OM', 'PK', 'PS', 'PH', 'QA', 'SA',
        'SG', 'KR', 'LK', 'SY', 'TW', 'TJ', 'TH', 'TL', 'TR', 'TM', 'AE', 'UZ',
        'VN', 'YE', 'CY'
    ],

    'Europe': [
        'AL', 'AD', 'AT', 'BY', 'BE', 'BA', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE',
        'FO', 'FI', 'FR', 'DE', 'GI', 'GR', 'GG', 'HU', 'IS', 'IE', 'IM', 'IT',
        'JE', 'XK', 'LV', 'LI', 'LT', 'LU', 'MT', 'MD', 'MC', 'ME', 'NL', 'MK',
        'NO', 'PL', 'PT', 'RO', 'RU', 'SM', 'RS', 'SK', 'SI', 'ES', 'SJ', 'SE',
        'CH', 'UA', 'GB', 'VA', 'AX', 'SG', 'US', 'CA'
    ],

    'Africa': [
        'DZ', 'AO', 'BJ', 'BW', 'BF', 'BI', 'CM', 'CV', 'CF', 'TD', 'KM', 'CG',
        'CD', 'DJ', 'EG', 'GQ', 'ER', 'SZ', 'ET', 'GA', 'GM', 'GH', 'GN', 'GW',
        'CI', 'KE', 'LS', 'LR', 'LY', 'MG', 'MW', 'ML', 'MR', 'MU', 'YT', 'MA',
        'MZ', 'NA', 'NE', 'NG', 'RE', 'RW', 'SH', 'ST', 'SN', 'SC', 'SL', 'SO',
        'ZA', 'SS', 'SD', 'TZ', 'TG', 'TN', 'UG', 'EH', 'ZM', 'ZW'
    ],

    // North America, including Central America, the Caribbean, Mexico and
    // Greenland.
    'North America': [
        'AI', 'AG', 'AW', 'BS', 'BB', 'BZ', 'BM', 'BQ', 'VG', 'CA', 'KY', 'CR',
        'CU', 'CW', 'DM', 'DO', 'SV', 'GL', 'GD', 'GP', 'GT', 'HT', 'HN', 'JM',
        'MQ', 'MX', 'MS', 'NI', 'PA', 'PR', 'BL', 'KN', 'LC', 'MF', 'PM', 'VC',
        'SX', 'TT', 'TC', 'US', 'VI', 'AN'
    ],

    'South America': [
        'AR', 'BO', 'BR', 'CL', 'CO', 'EC', 'FK', 'GF', 'GY', 'PY', 'PE', 'SR',
        'UY', 'VE',
    ],
}

// Flat lookup { CODE: region }, frozen so it can't be mutated at runtime.
export const ISO_ALPHA2_TO_REGION = Object.freeze(
    Object.entries(REGION_COUNTRIES).reduce((acc, [region, codes]) => {
        codes.forEach(code => { acc[code] = region })
        return acc
    }, {})
)

// Reduce a region name to alphanumerics only, lowercased. This lets a deep link
// separate words with a space, hyphen or underscore (or nothing) — "North
// America", "north-america", "north_america" and "northamerica" all match — so
// partners never have to worry about %20-encoding spaces in the URL.
const regionKey = (value) => String(value).toLowerCase().replace(/[^a-z0-9]/g, '')

// Region names keyed by their alphanumeric form. Names are all > 2 chars, so
// there is no collision with 2-letter country codes.
const REGION_BY_KEY = Object.keys(REGION_COUNTRIES).reduce((acc, region) => {
    acc[regionKey(region)] = region
    return acc
}, {})

// Resolve a deep-link `code` to a region string, or '' when unrecognised.
// Accepts EITHER a continent/region name (e.g. "Asia", "north-america") OR an
// ISO 3166-1 alpha-2 country code (e.g. "AU", "jp"), any casing/spacing.
export const resolveRegion = (value) => {
    if (!value) return ''

    const trimmed = String(value).trim()
    if (!trimmed) return ''

    // Region name (separator-agnostic) takes precedence over a country code.
    const by_name = REGION_BY_KEY[regionKey(trimmed)]
    if (by_name) return by_name

    return ISO_ALPHA2_TO_REGION[trimmed.toUpperCase()] || ''
}

// Resolve an ISO alpha-2 code to its English country name (e.g. "SG" ->
// "Singapore"), used for the transit badge (transit is always a country code).
// Uses Intl.DisplayNames where available and falls back to the raw code on
// older browsers or unknown/invalid input.
export const resolveCountryName = (code) => {
    if (!code) return ''

    const normalized = String(code).trim().toUpperCase()
    if (!/^[A-Z]{2}$/.test(normalized)) return ''

    try {
        if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
            const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(normalized)
            if (name && name !== normalized) return name
        }
    } catch (e) {
        // Intl.DisplayNames unsupported (older iOS) — fall through to the code.
    }

    return normalized
}

export default resolveRegion
